'use client';

import React, { useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Calendar } from 'lucide-react';
import { BlogList } from '@/types';
import { stripHtmlTags, toUrlSlug } from '@/lib/utils';

interface BlogCardProps {
  blog: BlogList;
}

const BlogCardComponent: React.FC<BlogCardProps> = ({ blog }) => {
  const blogSlug = useMemo(() => {
    // Use the slug from backend if available, otherwise convert title
    if (blog.slug) {
      // If slug is already a proper URL slug, use it; otherwise convert it
      return blog.slug.includes(' ') ? toUrlSlug(blog.slug) : blog.slug;
    }
    return toUrlSlug(blog.title);
  }, [blog.slug, blog.title]);

  const formattedDate = useMemo(() => {
    const dateStr = blog.publishedAt || blog.createdAt;
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }, [blog.publishedAt, blog.createdAt]);

  const shortContent = useMemo(() => {
    const rawContent = blog.excerpt || blog.content || '';
    const content = stripHtmlTags(rawContent);
    return content.substring(0, 150) + (content.length > 150 ? '...' : '');
  }, [blog.content, blog.excerpt]);

  return (
    <Link href={`/blog/${blogSlug}`} className="group">
      <div className="overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg bg-white rounded-xl border border-gray-200">
        <div className="aspect-video w-full relative overflow-hidden">
          <Image
            src={blog.coverImage || blog.heroImageUrl || '/hero-image.jpeg'}
            alt={blog.title}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            quality={70}
            priority={false}
          />
          <div className="absolute top-0 left-0 bottom-0 right-0 bg-gradient-to-b from-black/50 via-20% via-transparent to-black/20 p-2">
            {blog.category && (
              <span className="bg-primary/10 border border-primary/20 text-primary text-xs font-semibold px-3 py-0.5 rounded-full">
                {blog.category}
              </span>
            )}
          </div>
        </div>

        <div className="p-6">
          <h3 className="text-xl font-semibold line-clamp-2 text-gray-900 mb-3 group-hover:text-primary transition-colors">
            {stripHtmlTags(blog.title)}
          </h3>

          <p className="line-clamp-3 mb-4 text-gray-600 text-sm">
            {shortContent}
          </p>

          <div className="flex items-center text-xs text-gray-500">
            <Calendar className="w-4 h-4 mr-1" />
            <span>{formattedDate}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

const BlogCard = React.memo(BlogCardComponent, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.blog) === JSON.stringify(nextProps.blog);
});

export default BlogCard;
